import {
    CmsCtaTarget,
    CmsDraftPageContent,
    CmsPageModuleContent,
    CmsPageModuleType,
    CmsQuoteSectionLayout,
} from '@ndvr/cms-api';

const SINGLETON_MODULE_TYPES = new Set<CmsPageModuleType>([
    CmsPageModuleType.ADVISOR_PROFILE,
    CmsPageModuleType.CONTACT_SECTION,
    CmsPageModuleType.LEAD_FORM,
    CmsPageModuleType.IMPORTANT_DISCLOSURES,
    CmsPageModuleType.FAQ,
]);

const MODULE_PAYLOAD_KEY = {
    [CmsPageModuleType.HERO]: 'hero',
    [CmsPageModuleType.CTA]: 'cta',
    [CmsPageModuleType.ADVISOR_PROFILE]: 'advisorProfile',
    [CmsPageModuleType.CONTACT_SECTION]: 'contactSection',
    [CmsPageModuleType.LEAD_FORM]: 'leadForm',
    [CmsPageModuleType.INFO_CARD_GRID]: 'infoCardGrid',
    [CmsPageModuleType.MEDIA_TEXT_SECTION]: 'mediaTextSection',
    [CmsPageModuleType.PROCESS_STEPS]: 'processSteps',
    [CmsPageModuleType.QUOTE_SECTION]: 'quoteSection',
    [CmsPageModuleType.RESOURCE_LIST]: 'resourceList',
    [CmsPageModuleType.IMPORTANT_DISCLOSURES]: 'importantDisclosures',
    [CmsPageModuleType.FAQ]: 'faq',
} satisfies Record<CmsPageModuleType, keyof CmsPageModuleContent>;

const MODULE_PAYLOAD_KEYS = Object.values(MODULE_PAYLOAD_KEY);
const EMPTY_MODULE_PAYLOAD = {
    [CmsPageModuleType.HERO]: { hero: {} as NonNullable<CmsPageModuleContent['hero']> },
    [CmsPageModuleType.CTA]: { cta: {} as NonNullable<CmsPageModuleContent['cta']> },
    [CmsPageModuleType.ADVISOR_PROFILE]: {
        advisorProfile: {} as NonNullable<CmsPageModuleContent['advisorProfile']>,
    },
    [CmsPageModuleType.CONTACT_SECTION]: {
        contactSection: {} as NonNullable<CmsPageModuleContent['contactSection']>,
    },
    [CmsPageModuleType.LEAD_FORM]: { leadForm: {} as NonNullable<CmsPageModuleContent['leadForm']> },
    [CmsPageModuleType.INFO_CARD_GRID]: {
        infoCardGrid: {} as NonNullable<CmsPageModuleContent['infoCardGrid']>,
    },
    [CmsPageModuleType.MEDIA_TEXT_SECTION]: {
        mediaTextSection: {} as NonNullable<CmsPageModuleContent['mediaTextSection']>,
    },
    [CmsPageModuleType.PROCESS_STEPS]: {
        processSteps: {} as NonNullable<CmsPageModuleContent['processSteps']>,
    },
    [CmsPageModuleType.QUOTE_SECTION]: {
        quoteSection: {} as NonNullable<CmsPageModuleContent['quoteSection']>,
    },
    [CmsPageModuleType.RESOURCE_LIST]: {
        resourceList: {} as NonNullable<CmsPageModuleContent['resourceList']>,
    },
    [CmsPageModuleType.IMPORTANT_DISCLOSURES]: {
        importantDisclosures: {} as NonNullable<CmsPageModuleContent['importantDisclosures']>,
    },
    [CmsPageModuleType.FAQ]: { faq: {} as NonNullable<CmsPageModuleContent['faq']> },
} satisfies Record<CmsPageModuleType, Partial<CmsPageModuleContent>>;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const URL_SCHEME_PATTERN = /^[a-z][a-z\d+\-.]*:/i;
const DISCLOSURE_FOOTNOTE_KEY_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const DISCLOSURE_FOOTNOTE_REFERENCE_PATTERN = /\[\[([a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?)]]/g;
const DISCLOSURE_FOOTNOTE_BRACKET_PATTERN = /\[\[([^\]]*)]]/g;

type CmsValidationMode = 'save' | 'submit';
type CmsValidationIssueSeverity = 'blocking' | 'recommendation';

export interface CmsValidationIssue {
    moduleIndex?: number;
    path?: string;
    message: string;
    severity: CmsValidationIssueSeverity;
}

export interface CmsValidationResult {
    issues: CmsValidationIssue[];
    isValid: boolean;
}

type ModuleEntry = {
    module: CmsPageModuleContent;
    index: number;
};

export const hasText = (value?: string): boolean => !!value?.trim();

const hasTextList = (values?: string[]): boolean => !!values?.some(hasText);

const hasGridItems = (items?: NonNullable<NonNullable<CmsPageModuleContent['infoCardGrid']>['items']>): boolean =>
    !!items?.some(hasGridItemContent);

const hasProcessSteps = (items?: NonNullable<NonNullable<CmsPageModuleContent['processSteps']>['steps']>): boolean =>
    !!items?.some(hasProcessStepContent);

const hasResourceItems = (items?: NonNullable<NonNullable<CmsPageModuleContent['resourceList']>['items']>): boolean =>
    !!items?.some(hasResourceItemContent);

const hasImportantDisclosureParagraphs = (
    items?: NonNullable<NonNullable<CmsPageModuleContent['importantDisclosures']>['paragraphs']>,
): boolean => !!items?.some(hasImportantDisclosureParagraphContent);

const hasImportantDisclosureFootnotes = (
    items?: NonNullable<NonNullable<CmsPageModuleContent['importantDisclosures']>['footnotes']>,
): boolean => !!items?.some(hasImportantDisclosureFootnoteContent);

const hasFaqItems = (items?: NonNullable<NonNullable<CmsPageModuleContent['faq']>['items']>): boolean =>
    !!items?.some(hasFaqItemContent);

export const isEmptyOptionalModule = (module: CmsPageModuleContent): boolean => {
    if (module.moduleType === CmsPageModuleType.ADVISOR_PROFILE) {
        const profile = module.advisorProfile;
        return (
            !hasText(profile?.name) &&
            !hasText(profile?.title) &&
            !hasTextList(profile?.credentials) &&
            !hasText(profile?.bio) &&
            !hasText(profile?.phone) &&
            !hasText(profile?.email) &&
            !hasText(profile?.calendarUrl) &&
            !hasText(profile?.linkedInUrl) &&
            !hasText(profile?.location) &&
            !hasText(profile?.imageAssetId)
        );
    }
    if (module.moduleType === CmsPageModuleType.CONTACT_SECTION) {
        const section = module.contactSection;
        return (
            !hasText(section?.heading) &&
            !hasText(section?.body) &&
            !hasText(section?.phone) &&
            !hasText(section?.email) &&
            !hasText(section?.calendarUrl) &&
            !hasTextList(section?.addressLines) &&
            !hasText(section?.ctaLabel)
        );
    }
    if (module.moduleType === CmsPageModuleType.LEAD_FORM) {
        const form = module.leadForm;
        return (
            !hasText(form?.destinationEmail) &&
            !hasText(form?.eyebrow) &&
            !hasText(form?.heading) &&
            !hasText(form?.body) &&
            !hasText(form?.submitLabel) &&
            !hasText(form?.successMessage)
        );
    }
    if (module.moduleType === CmsPageModuleType.INFO_CARD_GRID) {
        const grid = module.infoCardGrid;
        return !hasText(grid?.eyebrow) && !hasText(grid?.heading) && !hasText(grid?.body) && !hasGridItems(grid?.items);
    }
    if (module.moduleType === CmsPageModuleType.MEDIA_TEXT_SECTION) {
        const section = module.mediaTextSection;
        return (
            !hasText(section?.eyebrow) &&
            !hasText(section?.heading) &&
            !hasText(section?.body) &&
            !hasText(section?.imageAssetId) &&
            !hasText(section?.ctaLabel) &&
            !hasText(section?.ctaUrl)
        );
    }
    if (module.moduleType === CmsPageModuleType.PROCESS_STEPS) {
        const section = module.processSteps;
        return (
            !hasText(section?.eyebrow) &&
            !hasText(section?.heading) &&
            !hasText(section?.body) &&
            !hasProcessSteps(section?.steps)
        );
    }
    if (module.moduleType === CmsPageModuleType.QUOTE_SECTION) {
        const section = module.quoteSection;
        return (
            !hasText(section?.eyebrow) &&
            !hasText(section?.quote) &&
            !hasText(section?.attributionName) &&
            !hasText(section?.attributionTitle) &&
            !hasText(section?.imageAssetId) &&
            !hasText(section?.ctaLabel) &&
            !hasText(section?.ctaUrl)
        );
    }
    if (module.moduleType === CmsPageModuleType.RESOURCE_LIST) {
        const section = module.resourceList;
        return (
            !hasText(section?.eyebrow) &&
            !hasText(section?.heading) &&
            !hasText(section?.body) &&
            !hasResourceItems(section?.items)
        );
    }
    if (module.moduleType === CmsPageModuleType.IMPORTANT_DISCLOSURES) {
        const section = module.importantDisclosures;
        return (
            !hasText(section?.heading) &&
            !hasImportantDisclosureParagraphs(section?.paragraphs) &&
            !hasImportantDisclosureFootnotes(section?.footnotes)
        );
    }
    if (module.moduleType === CmsPageModuleType.FAQ) {
        const faq = module.faq;
        return !hasText(faq?.eyebrow) && !hasText(faq?.heading) && !hasFaqItems(faq?.items);
    }
    return false;
};

export const draftContentForSave = (draft: CmsDraftPageContent): CmsDraftPageContent => ({
    ...draft,
    modules: (draft.modules ?? [])
        .map(withDefaultModulePayload)
        .map(normalizeCmsModuleTextLists)
        .map(normalizeCmsModuleUrls),
});

export const validateCmsDraftForSave = (draft: CmsDraftPageContent): CmsValidationResult =>
    validateCmsDraft(draft, 'save');

export const validateCmsDraftForSubmit = (draft: CmsDraftPageContent): CmsValidationResult =>
    validateCmsDraft(draft, 'submit');

export const cmsValidationIssuesByModuleIndex = (issues: CmsValidationIssue[]): Record<number, CmsValidationIssue[]> =>
    issues.reduce<Record<number, CmsValidationIssue[]>>((acc, issue) => {
        if (issue.moduleIndex == null) {
            return acc;
        }
        acc[issue.moduleIndex] = [...(acc[issue.moduleIndex] ?? []), issue];
        return acc;
    }, {});

const validateCmsDraft = (draft: CmsDraftPageContent, mode: CmsValidationMode): CmsValidationResult => {
    const issues: CmsValidationIssue[] = [];
    const modules = draftContentForSave(draft).modules ?? [];
    const entries = modules.map((module, index) => ({ module, index }));

    validateModuleShape(entries, issues);
    validateSingletons(entries, issues);
    validateDisclosureFootnoteKeys(entries, issues);
    validateDisclosureFootnoteReferences(entries, issues);

    if (mode === 'submit') {
        validateSubmitRelationships(entries, issues);
        validateSubmitContent(entries, issues);
    }

    return { issues, isValid: !issues.some((issue) => issue.severity === 'blocking') };
};

const validateModuleShape = (entries: ModuleEntry[], issues: CmsValidationIssue[]) => {
    entries.forEach(({ module, index }) => {
        if (!module.moduleType) {
            addIssue(issues, index, 'moduleType', 'Choose a section type.');
            return;
        }

        const expectedPayloadKey = MODULE_PAYLOAD_KEY[module.moduleType];
        const payloadCount = MODULE_PAYLOAD_KEYS.filter((key) => module[key] != null).length;
        if (payloadCount !== 1 || module[expectedPayloadKey] == null) {
            addIssue(
                issues,
                index,
                String(expectedPayloadKey),
                'This section has mismatched content. Remove and add it again.',
            );
        }
    });
};

const validateSingletons = (entries: ModuleEntry[], issues: CmsValidationIssue[]) => {
    for (const moduleType of SINGLETON_MODULE_TYPES) {
        const matchingEntries = entries.filter(({ module }) => module.moduleType === moduleType);
        matchingEntries.slice(1).forEach(({ index }) => {
            addIssue(issues, index, 'moduleType', `Only one ${moduleType} section is allowed.`);
        });
    }
};

const validateSubmitRelationships = (entries: ModuleEntry[], issues: CmsValidationIssue[]) => {
    const hasLeadForm = entries.some(({ module }) => module.moduleType === CmsPageModuleType.LEAD_FORM);
    const ctaTargetsLeadForm = entries.some(
        ({ module }) => module.moduleType === CmsPageModuleType.CTA && ctaTarget(module) === CmsCtaTarget.LEAD_FORM,
    );

    if (ctaTargetsLeadForm && !hasLeadForm) {
        addRecommendation(issues, undefined, 'modules', 'Add a lead form or change the CTA target.');
    }
};

const validateDisclosureFootnoteKeys = (entries: ModuleEntry[], issues: CmsValidationIssue[]) => {
    entries.forEach(({ module, index }) => {
        if (module.moduleType !== CmsPageModuleType.IMPORTANT_DISCLOSURES) {
            return;
        }

        const seenKeys = new Set<string>();
        module.importantDisclosures?.footnotes?.forEach((item, itemIndex) => {
            if (!hasImportantDisclosureFootnoteContent(item)) {
                return;
            }
            const prefix = `importantDisclosures.footnotes.${itemIndex}`;
            const key = item.key?.trim();
            if (!key) {
                addIssue(issues, index, `${prefix}.key`, 'Add a footnote key.');
            } else if (!DISCLOSURE_FOOTNOTE_KEY_PATTERN.test(key)) {
                addIssue(issues, index, `${prefix}.key`, 'Use lowercase letters, numbers, and hyphens only.');
            } else if (seenKeys.has(key)) {
                addIssue(issues, index, `${prefix}.key`, 'Use a unique footnote key.');
            }
            if (key) {
                seenKeys.add(key);
            }
            requireText(issues, index, `${prefix}.body`, item.body, 'Add footnote text.');
        });
    });
};

const validateDisclosureFootnoteReferences = (entries: ModuleEntry[], issues: CmsValidationIssue[]) => {
    const availableKeys = new Set<string>();
    const referencedKeys = new Set<string>();
    const warnedUnusedKeys = new Set<string>();
    entries.forEach(({ module }) => {
        if (module.moduleType !== CmsPageModuleType.IMPORTANT_DISCLOSURES) {
            return;
        }
        module.importantDisclosures?.footnotes?.forEach((item) => {
            const key = item.key?.trim();
            if (key && hasText(item.body)) {
                availableKeys.add(key);
            }
        });
    });

    entries.forEach(({ module, index }) => {
        collectDisclosureReferenceFields(module).forEach(({ path, value }) => {
            invalidDisclosureReferenceTokens(value).forEach((token) => {
                addRecommendation(issues, index, path, `Use [[lowercase-hyphen-key]] syntax for ${token}.`);
            });
            disclosureReferenceKeys(value).forEach((key) => {
                referencedKeys.add(key);
                if (!availableKeys.has(key)) {
                    addIssue(issues, index, path, `Add a matching disclosure footnote for [[${key}]].`);
                }
            });
        });
    });

    entries.forEach(({ module, index }) => {
        if (module.moduleType !== CmsPageModuleType.IMPORTANT_DISCLOSURES) {
            return;
        }
        module.importantDisclosures?.footnotes?.forEach((item, itemIndex) => {
            const key = item.key?.trim();
            if (key && hasText(item.body) && !referencedKeys.has(key) && !warnedUnusedKeys.has(key)) {
                warnedUnusedKeys.add(key);
                addRecommendation(
                    issues,
                    index,
                    `importantDisclosures.footnotes.${itemIndex}.key`,
                    `Footnote [[${key}]] is not referenced in page text.`,
                );
            }
        });
    });
};

const validateSubmitContent = (entries: ModuleEntry[], issues: CmsValidationIssue[]) => {
    entries.forEach(({ module, index }) => {
        if (module.moduleType === CmsPageModuleType.HERO) {
            requireText(issues, index, 'hero.headline', module.hero?.headline, 'Add a hero headline.');
        }
        if (module.moduleType === CmsPageModuleType.CTA) {
            requireText(issues, index, 'cta.label', module.cta?.label, 'Add a CTA button label.');
            if (ctaTarget(module) === CmsCtaTarget.URL) {
                validateUrl(issues, index, 'cta.url', module.cta?.url, 'CTA URL');
            } else if (hasText(module.cta?.url)) {
                addRecommendation(issues, index, 'cta.url', 'Lead-form CTAs should not include a URL.');
            }
        }
        if (module.moduleType === CmsPageModuleType.ADVISOR_PROFILE) {
            validateOptionalUrl(issues, index, 'advisorProfile.calendarUrl', module.advisorProfile?.calendarUrl);
            validateOptionalUrl(issues, index, 'advisorProfile.linkedInUrl', module.advisorProfile?.linkedInUrl);
        }
        if (module.moduleType === CmsPageModuleType.CONTACT_SECTION) {
            validateOptionalUrl(issues, index, 'contactSection.calendarUrl', module.contactSection?.calendarUrl);
        }
        if (module.moduleType === CmsPageModuleType.LEAD_FORM) {
            validateEmail(
                issues,
                index,
                'leadForm.destinationEmail',
                module.leadForm?.destinationEmail,
                'Lead-form destination email',
                true,
            );
        }
        if (module.moduleType === CmsPageModuleType.INFO_CARD_GRID) {
            module.infoCardGrid?.items?.forEach((item, itemIndex) => {
                if (!hasGridItemContent(item)) {
                    return;
                }
                const prefix = `infoCardGrid.items.${itemIndex}`;
                validateOptionalUrl(issues, index, `${prefix}.ctaUrl`, item.ctaUrl);
                if (hasText(item.ctaLabel) && !hasText(item.ctaUrl)) {
                    addRecommendation(issues, index, `${prefix}.ctaUrl`, 'Add a card button URL or remove the label.');
                }
            });
        }
        if (module.moduleType === CmsPageModuleType.MEDIA_TEXT_SECTION) {
            validateOptionalUrl(issues, index, 'mediaTextSection.ctaUrl', module.mediaTextSection?.ctaUrl);
            if (hasText(module.mediaTextSection?.ctaLabel) && !hasText(module.mediaTextSection?.ctaUrl)) {
                addRecommendation(issues, index, 'mediaTextSection.ctaUrl', 'Add a button URL or remove the label.');
            }
        }
        if (module.moduleType === CmsPageModuleType.QUOTE_SECTION) {
            requireText(issues, index, 'quoteSection.quote', module.quoteSection?.quote, 'Add quote text.');
            validateOptionalUrl(issues, index, 'quoteSection.ctaUrl', module.quoteSection?.ctaUrl);
            if (hasText(module.quoteSection?.ctaLabel) && !hasText(module.quoteSection?.ctaUrl)) {
                addRecommendation(issues, index, 'quoteSection.ctaUrl', 'Add a button URL or remove the label.');
            }
        }
        if (module.moduleType === CmsPageModuleType.RESOURCE_LIST) {
            module.resourceList?.items?.forEach((item, itemIndex) => {
                const prefix = `resourceList.items.${itemIndex}`;
                requireText(issues, index, `${prefix}.linkLabel`, item.linkLabel, 'Add a resource link label.');
                validateUrl(issues, index, `${prefix}.url`, item.url, 'Resource URL');
            });
        }
        if (module.moduleType === CmsPageModuleType.IMPORTANT_DISCLOSURES) {
            requireText(
                issues,
                index,
                'importantDisclosures.heading',
                module.importantDisclosures?.heading,
                'Add an important disclosures heading.',
            );
            module.importantDisclosures?.paragraphs?.forEach((item, itemIndex) => {
                if (!hasImportantDisclosureParagraphContent(item)) {
                    return;
                }
                const prefix = `importantDisclosures.paragraphs.${itemIndex}`;
                requireText(issues, index, `${prefix}.body`, item.body, 'Add disclosure paragraph text.');
            });
        }
        if (module.moduleType === CmsPageModuleType.FAQ) {
            module.faq?.items?.forEach((item, itemIndex) => {
                const prefix = `faq.items.${itemIndex}`;
                requireText(issues, index, `${prefix}.question`, item.question, 'Add an FAQ question.');
                requireText(issues, index, `${prefix}.answer`, item.answer, 'Add an FAQ answer.');
            });
        }
    });
};

const withDefaultModulePayload = (module: CmsPageModuleContent): CmsPageModuleContent => {
    if (!module.moduleType) {
        return module;
    }
    const payloadKey = MODULE_PAYLOAD_KEY[module.moduleType];
    if (module[payloadKey] != null) {
        return module;
    }
    return { ...module, ...EMPTY_MODULE_PAYLOAD[module.moduleType] };
};

const normalizeCmsModuleUrls = (module: CmsPageModuleContent): CmsPageModuleContent => {
    if (module.moduleType === CmsPageModuleType.CTA) {
        return { ...module, cta: { ...module.cta!, url: normalizeHttpsUrl(module.cta?.url) } };
    }
    if (module.moduleType === CmsPageModuleType.ADVISOR_PROFILE) {
        return {
            ...module,
            advisorProfile: {
                ...module.advisorProfile!,
                calendarUrl: normalizeHttpsUrl(module.advisorProfile?.calendarUrl),
                linkedInUrl: normalizeHttpsUrl(module.advisorProfile?.linkedInUrl),
            },
        };
    }
    if (module.moduleType === CmsPageModuleType.CONTACT_SECTION) {
        return {
            ...module,
            contactSection: {
                ...module.contactSection!,
                calendarUrl: normalizeHttpsUrl(module.contactSection?.calendarUrl),
            },
        };
    }
    if (module.moduleType === CmsPageModuleType.INFO_CARD_GRID) {
        return {
            ...module,
            infoCardGrid: {
                ...module.infoCardGrid!,
                items: module.infoCardGrid?.items?.map((item) => ({
                    ...item,
                    ctaUrl: normalizeHttpsUrl(item.ctaUrl),
                })),
            },
        };
    }
    if (module.moduleType === CmsPageModuleType.MEDIA_TEXT_SECTION) {
        return {
            ...module,
            mediaTextSection: {
                ...module.mediaTextSection!,
                ctaUrl: normalizeHttpsUrl(module.mediaTextSection?.ctaUrl),
            },
        };
    }
    if (module.moduleType === CmsPageModuleType.QUOTE_SECTION) {
        return {
            ...module,
            quoteSection: {
                ...module.quoteSection!,
                imageAssetId:
                    module.quoteSection?.layout === CmsQuoteSectionLayout.CENTERED
                        ? undefined
                        : module.quoteSection?.imageAssetId,
                ctaUrl: normalizeHttpsUrl(module.quoteSection?.ctaUrl),
            },
        };
    }
    if (module.moduleType === CmsPageModuleType.RESOURCE_LIST) {
        return {
            ...module,
            resourceList: {
                ...module.resourceList!,
                items: module.resourceList?.items?.map((item) => ({
                    ...item,
                    url: normalizeHttpsUrl(item.url),
                })),
            },
        };
    }
    return module;
};

const normalizeCmsModuleTextLists = (module: CmsPageModuleContent): CmsPageModuleContent => {
    if (module.moduleType === CmsPageModuleType.ADVISOR_PROFILE) {
        return {
            ...module,
            advisorProfile: {
                ...module.advisorProfile!,
                credentials: module.advisorProfile?.credentials?.filter(hasText),
            },
        };
    }
    return module;
};

const collectDisclosureReferenceFields = (module: CmsPageModuleContent): { path: string; value?: string }[] => {
    const fields: { path: string; value?: string }[] = [];
    fields.push({ path: 'hero.eyebrow', value: module.hero?.eyebrow });
    fields.push({ path: 'hero.headline', value: module.hero?.headline });
    fields.push({ path: 'hero.body', value: module.hero?.body });
    fields.push({ path: 'advisorProfile.title', value: module.advisorProfile?.title });
    fields.push({ path: 'advisorProfile.bio', value: module.advisorProfile?.bio });
    fields.push({ path: 'contactSection.body', value: module.contactSection?.body });
    fields.push({ path: 'leadForm.eyebrow', value: module.leadForm?.eyebrow });
    fields.push({ path: 'leadForm.heading', value: module.leadForm?.heading });
    fields.push({ path: 'leadForm.body', value: module.leadForm?.body });
    fields.push({ path: 'infoCardGrid.eyebrow', value: module.infoCardGrid?.eyebrow });
    fields.push({ path: 'infoCardGrid.heading', value: module.infoCardGrid?.heading });
    fields.push({ path: 'infoCardGrid.body', value: module.infoCardGrid?.body });
    module.infoCardGrid?.items?.forEach((item, itemIndex) => {
        fields.push({ path: `infoCardGrid.items.${itemIndex}.title`, value: item.title });
        fields.push({ path: `infoCardGrid.items.${itemIndex}.body`, value: item.body });
    });
    fields.push({ path: 'mediaTextSection.eyebrow', value: module.mediaTextSection?.eyebrow });
    fields.push({ path: 'mediaTextSection.heading', value: module.mediaTextSection?.heading });
    fields.push({ path: 'mediaTextSection.body', value: module.mediaTextSection?.body });
    fields.push({ path: 'processSteps.eyebrow', value: module.processSteps?.eyebrow });
    fields.push({ path: 'processSteps.heading', value: module.processSteps?.heading });
    fields.push({ path: 'processSteps.body', value: module.processSteps?.body });
    module.processSteps?.steps?.forEach((step, stepIndex) => {
        fields.push({ path: `processSteps.steps.${stepIndex}.title`, value: step.title });
        fields.push({ path: `processSteps.steps.${stepIndex}.body`, value: step.body });
    });
    fields.push({ path: 'quoteSection.eyebrow', value: module.quoteSection?.eyebrow });
    fields.push({ path: 'quoteSection.quote', value: module.quoteSection?.quote });
    fields.push({ path: 'quoteSection.attributionTitle', value: module.quoteSection?.attributionTitle });
    fields.push({ path: 'resourceList.eyebrow', value: module.resourceList?.eyebrow });
    fields.push({ path: 'resourceList.heading', value: module.resourceList?.heading });
    fields.push({ path: 'resourceList.body', value: module.resourceList?.body });
    module.resourceList?.items?.forEach((item, itemIndex) => {
        fields.push({ path: `resourceList.items.${itemIndex}.title`, value: item.title });
        fields.push({ path: `resourceList.items.${itemIndex}.description`, value: item.description });
    });
    fields.push({ path: 'faq.eyebrow', value: module.faq?.eyebrow });
    fields.push({ path: 'faq.heading', value: module.faq?.heading });
    module.faq?.items?.forEach((item, itemIndex) => {
        fields.push({ path: `faq.items.${itemIndex}.question`, value: item.question });
        fields.push({ path: `faq.items.${itemIndex}.answer`, value: item.answer });
    });
    return fields;
};

const disclosureReferenceKeys = (value?: string): string[] => {
    if (!value) {
        return [];
    }
    return Array.from(value.matchAll(DISCLOSURE_FOOTNOTE_REFERENCE_PATTERN), (match) => match[1]).filter(
        (key): key is string => !!key,
    );
};

const invalidDisclosureReferenceTokens = (value?: string): string[] => {
    if (!value) {
        return [];
    }
    return Array.from(value.matchAll(DISCLOSURE_FOOTNOTE_BRACKET_PATTERN), (match) => ({
        token: match[0],
        key: match[1],
    }))
        .filter(({ key }) => !key || !DISCLOSURE_FOOTNOTE_KEY_PATTERN.test(key))
        .map(({ token }) => token);
};

export const normalizeHttpsUrl = (value: string | undefined): string | undefined => {
    if (value == null) {
        return value;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return trimmedValue;
    }

    if (trimmedValue.startsWith('//')) {
        return `https:${trimmedValue}`;
    }

    if (/^http:\/\//i.test(trimmedValue)) {
        return trimmedValue.replace(/^http:\/\//i, 'https://');
    }

    if (URL_SCHEME_PATTERN.test(trimmedValue)) {
        return trimmedValue;
    }

    return `https://${trimmedValue}`;
};

export const cmsHttpsUrlValidationMessage = (value: string | undefined, label = 'URL'): string | undefined => {
    const trimmedValue = normalizeHttpsUrl(value)?.trim();
    if (!trimmedValue) {
        return undefined;
    }

    let url: URL;
    try {
        url = new URL(trimmedValue);
    } catch {
        return `${label} must be a valid URL.`;
    }

    if (url.protocol !== 'https:' || !url.hostname) {
        return `${label} must be an absolute https URL.`;
    }

    return undefined;
};

const ctaTarget = (module: CmsPageModuleContent): CmsCtaTarget => {
    if (module.cta?.target) {
        return module.cta.target;
    }
    return hasText(module.cta?.url) ? CmsCtaTarget.URL : CmsCtaTarget.LEAD_FORM;
};

const hasGridItemContent = (item: { title?: string; body?: string; ctaLabel?: string; ctaUrl?: string }): boolean =>
    hasText(item.title) || hasText(item.body) || hasText(item.ctaLabel) || hasText(item.ctaUrl);

const hasProcessStepContent = (item: { title?: string; body?: string }): boolean =>
    hasText(item.title) || hasText(item.body);

const hasResourceItemContent = (item: {
    title?: string;
    description?: string;
    linkLabel?: string;
    url?: string;
}): boolean => hasText(item.title) || hasText(item.description) || hasText(item.linkLabel) || hasText(item.url);

const hasImportantDisclosureParagraphContent = (item: { body?: string }): boolean => hasText(item.body);

const hasImportantDisclosureFootnoteContent = (item: { key?: string; label?: string; body?: string }): boolean =>
    hasText(item.key) || hasText(item.label) || hasText(item.body);

const hasFaqItemContent = (item: { question?: string; answer?: string }): boolean =>
    hasText(item.question) || hasText(item.answer);

const requireText = (
    issues: CmsValidationIssue[],
    moduleIndex: number | undefined,
    path: string,
    value: string | undefined,
    message: string,
) => {
    if (!hasText(value)) {
        addRecommendation(issues, moduleIndex, path, message);
    }
};

const validateOptionalUrl = (
    issues: CmsValidationIssue[],
    moduleIndex: number,
    path: string,
    value: string | undefined,
) => {
    if (hasText(value)) {
        validateUrl(issues, moduleIndex, path, value, 'URL');
    }
};

const validateEmail = (
    issues: CmsValidationIssue[],
    moduleIndex: number,
    path: string,
    value: string | undefined,
    label: string,
    required = false,
) => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
        if (required) {
            addRecommendation(issues, moduleIndex, path, `Add a ${label.toLowerCase()}.`);
        }
        return;
    }
    if (!EMAIL_PATTERN.test(trimmedValue)) {
        addIssue(issues, moduleIndex, path, `${label} must be a valid email address.`);
    }
};

const validateUrl = (
    issues: CmsValidationIssue[],
    moduleIndex: number | undefined,
    path: string,
    value: string | undefined,
    label: string,
) => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
        addRecommendation(issues, moduleIndex, path, `Add a ${label}.`);
        return;
    }

    const validationMessage = cmsHttpsUrlValidationMessage(trimmedValue, label);
    if (validationMessage) {
        addIssue(issues, moduleIndex, path, validationMessage);
    }
};

const addIssue = (
    issues: CmsValidationIssue[],
    moduleIndex: number | undefined,
    path: string | undefined,
    message: string,
    severity: CmsValidationIssueSeverity = 'blocking',
) => {
    issues.push({ moduleIndex, path, message, severity });
};

const addRecommendation = (
    issues: CmsValidationIssue[],
    moduleIndex: number | undefined,
    path: string | undefined,
    message: string,
) => addIssue(issues, moduleIndex, path, message, 'recommendation');
